import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Package, ShoppingBag } from "lucide-react";
import { Reveal } from "../Reveal";
import { useCart } from "../../context/CartContext";
import { eur } from "../../lib/api";

const DOT = { Grow: "#3FA34D", "A Bloom": "#D9412E", "B Bloom": "#3B63D6", PK: "#F08A24", Compact: "#9A7B5B", "Bongo Bloom": "#8B5CD6" };

const KitCard = ({ k, i }) => {
  const { add, setOpen } = useCart();
  const featured = k.id === "kit-200";
  return (
    <Reveal delay={i * 0.1} className="h-full">
      <motion.div whileHover={{ y: -8 }} data-testid={`kit-card-${k.id}`}
        className={`relative h-full flex flex-col rounded-[12px] p-7 border ${featured ? "bg-[#F6EEDC] text-[#0E2A1C] border-[#F2B441]" : "bg-[#1B3A8C] border-[#F6EEDC]/25"}`}>
        {featured && <span className="absolute -top-3 left-7 rounded-full bg-[#F2B441] text-[#0E2A1C] px-3 py-1 font-mono-d text-[10px] tracking-[0.2em] font-bold">KIT RECOMENDADO</span>}
        <p className="font-mono-d text-[11px] tracking-[0.22em] text-[#F2B441] font-bold">KIT</p>
        <h3 className={`font-label text-4xl leading-none mt-2 ${featured ? "text-[#1B3A8C]" : "text-label-gold"}`}>{k.kit.title}</h3>
        <ul className="mt-6 space-y-2.5 text-sm flex-1">
          {k.kit.items.map(([n, q]) => (
            <li key={n} className={`flex items-center justify-between gap-3 border-b pb-2 ${featured ? "border-[#0E2A1C]/10" : "border-[#F6EEDC]/10"}`}>
              <span className="flex items-center gap-2.5"><i className="w-2.5 h-2.5 rounded-[3px]" style={{ background: DOT[n] }} />{n}</span>
              <span className="font-mono-d font-bold">{q}</span>
            </li>
          ))}
        </ul>
        <p className={`mt-5 text-sm flex items-center gap-2 ${featured ? "text-[#0E2A1C]/80" : "text-[#E3EADF]"}`}><Package className="w-4 h-4" /> {k.kit.envases} envases</p>
        <div className="mt-4 flex items-end justify-between gap-3">
          <div>
            <p data-testid={`kit-was-${k.id}`} className={`line-through font-mono-d text-sm ${featured ? "text-[#0E2A1C]/60" : "text-[#E3EADF]/80"}`}>{eur(k.kit.was)}</p>
            <p data-testid={`product-price-${k.id}`} className={`font-serif-d font-black text-4xl leading-none ${featured ? "text-[#B86A0F]" : "text-[#F2B441]"}`}>{eur(k.price)}</p>
            <p className={`text-xs mt-1 ${featured ? "text-[#0E2A1C]/70" : "text-[#E3EADF]"}`}>-10 % incluido</p>
          </div>
          <button data-testid={`product-add-to-cart-${k.id}`} onClick={() => { add(k, 1, true); setOpen(true); }} aria-label={`Añadir ${k.name}`}
            className={`w-14 h-14 rounded-full grid place-items-center transition-transform hover:scale-105 active:scale-95 ${featured ? "bg-[#0E2A1C] text-[#F2B441]" : "bg-[#F2B441] text-[#0E2A1C]"}`}>
            <ShoppingBag className="w-5 h-5" />
          </button>
        </div>
      </motion.div>
    </Reveal>
  );
};

export const JKits = ({ kits }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const rotY = useTransform(scrollYProgress, [0, 1], [-14, 10]);
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);
  if (!kits.length) return null;
  return (
    <section ref={ref} id="kits" data-testid="kits-section" className="relative py-24 md:py-36 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center mb-14 md:mb-20">
          <Reveal className="lg:col-span-5">
            <p className="font-mono-d text-[11px] tracking-[0.22em] uppercase text-[#8CD05A] mb-6">Kits de cultivo completos · 10 % dto.</p>
            <h2 className="font-serif-d font-black text-5xl md:text-7xl leading-[0.92] tracking-tight">
              Toda la selva <span className="italic font-semibold text-[#F2B441]">en una caja.</span>
            </h2>
            <p className="text-[#C9D6C2] mt-6 leading-relaxed max-w-md">Cultiva con IA, no te la juegues. Elige tu kit según el número de plantas, de 15 a 400: las seis fórmulas en las cantidades justas y con un 10 % de descuento ya aplicado.</p>
          </Reveal>
          <div className="lg:col-span-7" style={{ perspective: 1400 }}>
            <motion.div style={{ rotateY: rotY, y }} className="relative">
              <div className="absolute inset-x-[10%] bottom-[-6%] h-[18%] rounded-[50%] bg-black/50 blur-2xl" />
              <img src="/img/kit-caja.jpg" alt="Caja kit Jungle Nutrient" loading="lazy" className="relative w-full rounded-[10px] shadow-[0_40px_100px_-30px_rgba(0,0,0,0.8)]" />
            </motion.div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 items-stretch">
          {kits.map((k, i) => <KitCard key={k.id} k={k} i={i} />)}
        </div>
      </div>
    </section>
  );
};
